// Asked before planning sessions that push a subject past its volume horaire.
import React from 'react';
import { X, Gauge, AlertTriangle, Check, Loader2 } from 'lucide-react';
import type { VolumeCheck } from '../../services/scheduleService';

interface VolumeExceededModalProps {
  matiereName: string;
  volume: VolumeCheck;
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const fmt = (h: number): string => `${Math.round(h * 10) / 10}h`;

export const VolumeExceededModal: React.FC<VolumeExceededModalProps> = ({ matiereName, volume, isLoading = false, onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
      <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
            <Gauge className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Volume horaire dépassé</h3>
            <p className="text-sm text-gray-500">{matiereName}</p>
          </div>
        </div>
        <button onClick={onCancel} disabled={isLoading} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50">
          <X size={20} />
        </button>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="p-3 rounded-xl bg-gray-50 border border-gray-200">
            <p className="text-xs text-gray-500">Déjà planifié</p>
            <p className="text-lg font-semibold text-gray-900">{fmt(volume.alreadyScheduledHours)}</p>
          </div>
          <div className="p-3 rounded-xl bg-gray-50 border border-gray-200">
            <p className="text-xs text-gray-500">À ajouter</p>
            <p className="text-lg font-semibold text-gray-900">+{fmt(volume.plannedHours)}</p>
          </div>
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
            <p className="text-xs text-amber-700">Total</p>
            <p className="text-lg font-semibold text-amber-800">{fmt(volume.totalAfterHours)}</p>
          </div>
        </div>

        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            Le volume horaire prévu pour cette matière est de <span className="font-semibold">{fmt(volume.volumeHoraire)}</span>.
            Ce planning le dépasse de <span className="font-semibold">{fmt(volume.exceedsByHours)}</span>.
            Voulez-vous quand même créer ces séances ?
          </p>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
        <button onClick={onCancel} disabled={isLoading} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl transition-colors disabled:opacity-50">
          Annuler
        </button>
        <button onClick={onConfirm} disabled={isLoading} className="px-4 py-2.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-xl transition-colors disabled:opacity-50 shadow-sm flex items-center gap-2">
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check size={16} />}
          Oui, dépasser le volume
        </button>
      </div>
    </div>
  </div>
);

export default VolumeExceededModal;
